import React, {
    ChangeEvent, useCallback, useRef, useState,
} from "react";
import {
    Button, List, ListItem, Switch, TextField,
} from "@material-ui/core";
import { useTranslation } from "react-i18next";
import styled from "@emotion/styled";
import { Station } from "@optimusride/client-library/@types/grpc/station";
import { LatLon } from "@optimusride/client-library/grpc/shared_pb";
import RemoveCircleOutlineIcon from "@material-ui/icons/RemoveCircleOutline";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import { convertArrayToObject } from "../../common/utils";
import {
    SPACING_8, SPACING_16, SPACING_24, SPACING_32, SPACING_28, SPACING_2, SPACING_12,
} from "../../common/styles/spacing";
import { H5, H6 } from "../../common/styles/typography";
import {
    BLACK, WHITE, ORANGE_500, ORANGE_600, RED_500, GRAY_450, GREEN_700,
} from "../../common/styles/colors";

const STATION_EDITOR_FIELDS = [
    "name",
    "address1",
    "address2",
    "city",
    "state",
    "zip",
    "lat",
    "lng",
] as const;

const REQUIRED_FIELDS: { [keys: string]: boolean } = {
    name: true,
    lat: true,
    lng: true,
};

const STATION_EDITOR_TYPE = (type: string) => {
    if (type === "lat" || type === "lng") {
        return "number";
    }

    return "text";
};

const STATION_NON_TEXT_FIELDS = [
    "disabled",
];

const StationEditCreateForm = styled.div`
    display: grid;
    grid-template-areas: 
        "StationEditorContainer StationEditorContainer" 
        "EditorFormContainer    StationSelectorContainer" 
        "ButtonContainer        ButtonContainer";
    grid-template-columns: 1fr 50%;
`;

const EditorFormContainer = styled.div`
    grid-area: EditorFormContainer;

    ${STATION_EDITOR_FIELDS.map((field) => (`
        #station-editor-${field}-container {
            grid-area: ${field};
            margin-bottom: ${SPACING_24};
        }
    `)).join("\n")}

    .MuiFormControl-root {
        width: 100%;
    }

    #station-editor-state-container {
        margin: 0 ${SPACING_16};
    }

    display: grid;
    grid-template-areas: "name     name    name     name    name" 
                         "address1 address1    address1 address1    address1" 
                         "address2 address2    address2 address2    address2" 
                         "city     .    state    .    zip"
                         "lat      lat  .        lng  lng";
    padding: 0 ${SPACING_32} ${SPACING_16};
`;

const StationEditorContainer = styled.div`
    display: flex;
    grid-area: StationEditorContainer;

    margin: ${SPACING_16} ${SPACING_16} ${SPACING_8} ${SPACING_28} ;
    justify-content: space-between;
    flex-wrap: wrap;
    align-items: center;
`;

const ButtonContainer = styled.div`
    display: flex;

    grid-area: ButtonContainer;
    justify-content: space-around;

    padding: 0 ${SPACING_16} ${SPACING_32};
`;

const StationSelectorContainer = styled.div`
    display: flex;
    position: relative;
    grid-area: StationSelectorContainer;
`;

type StyledButtonProps = {
    bgcolor?: string;
    hovercolor?: string;
};

const StyledButton = styled(Button)`
    background-color: ${({ bgcolor = BLACK }: StyledButtonProps) => bgcolor};
    color: ${WHITE};
    border-radius: ${SPACING_8};
    flex-basis: 40%;

    &:hover {
        background-color: ${({ hovercolor = BLACK }: StyledButtonProps) => hovercolor};
    }
`;

const ErrorMessage = styled(H6)`
    color: ${RED_500};
    flex-basis: 100%;
    width: 100%;
`;

const StyledStationList = styled(List)`
    max-height: 90%;
    overflow-y: scroll;
`;

const StyledStationListItem = styled(ListItem)`
    border: solid ${SPACING_2};
    border-radius: ${SPACING_16};

    border-color: ${({ selected }: { selected: boolean }) => (selected ? ORANGE_500 : GRAY_450)};
    border-width: ${({ selected }) => (selected ? "medium" : "thin")};

    font-weight: ${({ selected }) => (selected ? "bold" : "normal")};

    margin-bottom: ${SPACING_16};

    svg {
        margin-left: auto;
    }
`;

const StyledAddIcon = styled(AddCircleOutlineIcon)`
    color: ${GREEN_700};
`;

const StyledDeleteIcon = styled(RemoveCircleOutlineIcon)`
    color: ${RED_500};
`;

const StyledListContainer = styled.div`
    margin-left: ${SPACING_12};

    &:last-of-type {
        margin-right: ${SPACING_12};
    }

    padding-right: ${SPACING_12};
`;

type StationObject = {
    "name"?: string;
    "address1"?: string;
    "address2"?: string;
    "city"?: string;
    "state"?: string;
    "zip"?: string;
    "lat"?: number | string;
    "lng"?: number | string;
    "disabled"?: boolean;
    "linksFromList"?: number[];
    "linksToList"?: number[];
};

/**
 * if an element exists in the list, remove it
 * if the element does not exist, add it
 * @param prev number[] array to add or remove
 * @param id number element to add or remove
 * @returns number[] new list with the element added or removed
 */
const addOrRemoveElementFromList = (prev: number[], id: number) => (
    prev.includes(id)
        ? prev.filter((_id) => _id !== id)
        : [...prev, id]
);

export default ({
    station, stationID,
    onExit, onUpdate,
    proceedText = "home.stationEditor.update",
    listOfSourceDestinationStations = [],
}: {
    station: StationObject;
    stationID: number | null;
    onExit: () => void;
    onUpdate: (station: Partial<Station>) => void;
    proceedText?: string;
    listOfSourceDestinationStations?: [number, string][]
}) => {
    const { t } = useTranslation();
    const stationEditorDetailsRef = useRef({
        ...convertArrayToObject([
            ...STATION_EDITOR_FIELDS,
            ...STATION_NON_TEXT_FIELDS,
        ], ""),
        ...station,
    });
    const [disabled, setDisabled] = useState(station.disabled);
    const [errorMessage, setErrorMessage] = useState("");
    const [selectedSourceStations, setSelectedSourceStations] = useState<number[]>(
        station?.linksFromList || [],
    );
    const [selectedDestinationStations, setSelectedDestinationStations] = useState<number[]>(
        station?.linksToList || [],
    );

    const handleOnChange = useCallback((
        e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
        field: keyof StationObject,
    ) => {
        setErrorMessage("");

        if (["lat", "lng"].includes(field)) {
            const value = parseFloat(e.target.value);

            (stationEditorDetailsRef.current[field] as string | number) = Number.isFinite(value)
                ? value : "";
        } else {
            (stationEditorDetailsRef.current[field] as string | number) = e.target.value;
        }
    }, [setErrorMessage]);

    const handleDisabledClick = useCallback(() => {
        setErrorMessage("");
        const newValue = !stationEditorDetailsRef.current.disabled;
        stationEditorDetailsRef.current.disabled = newValue;
        setDisabled((d) => !d);
    }, [setDisabled, setErrorMessage]);

    // toggles the element in the source station state when the user clicks on a station
    const addToSourceStationList = useCallback((id: number) => {
        setSelectedSourceStations((prev) => addOrRemoveElementFromList(prev, id));
    }, [setSelectedSourceStations]);

    // toggles the element in the destination station state when the user clicks on a station
    const addToDestinationStationList = useCallback((id: number) => {
        setSelectedDestinationStations((prev) => addOrRemoveElementFromList(prev, id));
    }, [setSelectedDestinationStations]);

    const STATION_EDITOR_TEXTFIELDS_OPTIONS = STATION_EDITOR_FIELDS.map(
        (field) => (
            {
                field,
                id: `station-editor-${field}`,
                label: `home.stationEditor.${field}`,
                value: stationEditorDetailsRef.current[field],
            }
        ),
    );

    const handleUpdate = useCallback(() => {
        const {
            name,
            address1,
            address2,
            city,
            state,
            zip,
            lat,
            lng,
        } = stationEditorDetailsRef.current;

        if (!name) {
            setErrorMessage(t("home.stationEditor.nameError"));
        } else if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
            setErrorMessage(t("home.stationEditor.latLonError"));
        } else {
            const latLon = new LatLon();
            const latValue = parseFloat(`${lat || 0}`);
            const lngValue = parseFloat(`${lng || 0}`);
            latLon.setLatitude(latValue);
            latLon.setLongitude(lngValue);

            if (latValue < -90 || latValue > 90) {
                setErrorMessage(t("home.stationEditor.latOutOfBounds"));
            } else if (lngValue < -180 || lngValue > 180) {
                setErrorMessage(t("home.stationEditor.lngOutOfBounds"));
            } else {
                onUpdate({
                    id: stationID === null ? undefined : stationID,
                    latLon,
                    nickname: name || "",
                    address1,
                    address2,
                    city,
                    state,
                    postalcode: zip,
                    disabled,
                    linksFromList: selectedSourceStations,
                    linksToList: selectedDestinationStations,
                });
            }
        }
    }, [setErrorMessage, onUpdate, disabled, selectedSourceStations, selectedDestinationStations]);

    return (
        <StationEditCreateForm>
            <StationEditorContainer>
                <H5>
                    {t("home.stationEditor.title")}
                </H5>
                <div>
                    <span>{t("home.stationEditor.inactive")}</span>
                    <Switch
                        color="primary"
                        checked={!disabled}
                        value={!disabled}
                        onChange={handleDisabledClick}
                    />
                    <span>{t("home.stationEditor.active")}</span>
                </div>
                <ErrorMessage>{errorMessage}</ErrorMessage>
            </StationEditorContainer>
            <EditorFormContainer>
                {STATION_EDITOR_TEXTFIELDS_OPTIONS.map(({
                    id, label, value, field,
                }) => (
                    <div id={`${id}-container`} key={field}>
                        <TextField
                            required={REQUIRED_FIELDS[field]}
                            key={id}
                            type={STATION_EDITOR_TYPE(field)}
                            id={id}
                            data-testid={id}
                            label={t(label)}
                            defaultValue={value}
                            onChange={(e) => handleOnChange(e, field)}
                            variant="standard"
                        />
                    </div>
                ))}

            </EditorFormContainer>
            <StationSelectorContainer>
                {/* Maps the list of possible source and destination stations out, in addition
                    filters the current station selected (or none if creation) from the list as
                    a station can't go to itself.
                */}
                <StyledListContainer>
                    <H6>{t("home.stationEditor.sourceTitle")}</H6>
                    <StyledStationList>
                        {listOfSourceDestinationStations
                            .filter(([id]) => id !== stationID)
                            .map(([id, nickname]) => (
                                <StyledStationListItem
                                    button
                                    key={`source-${id}`}
                                    selected={!!selectedSourceStations.includes(id)}
                                    onClick={() => addToSourceStationList(id)}
                                >
                                    {nickname}
                                    {!!selectedSourceStations.includes(id)
                                    && !station.linksFromList?.includes(id)
                                        ? <StyledAddIcon />
                                        : ""}
                                    {!selectedSourceStations?.includes(id)
                                    && station.linksFromList?.includes(id)
                                        ? <StyledDeleteIcon />
                                        : ""}
                                </StyledStationListItem>
                            ))}
                    </StyledStationList>
                </StyledListContainer>
                <StyledListContainer>
                    <H6>{t("home.stationEditor.destinationTitle")}</H6>
                    <StyledStationList>
                        {listOfSourceDestinationStations
                            .filter(([id]) => id !== stationID)
                            .map(([id, nickname]) => (
                                <StyledStationListItem
                                    button
                                    key={`destination-${id}`}
                                    selected={!!selectedDestinationStations.includes(id)}
                                    onClick={() => addToDestinationStationList(id)}
                                >
                                    {nickname}
                                    {!!selectedDestinationStations.includes(id)
                                    && !station.linksToList?.includes(id)
                                        ? <StyledAddIcon />
                                        : ""}
                                    {!selectedDestinationStations?.includes(id)
                                    && station.linksToList?.includes(id)
                                        ? <StyledDeleteIcon />
                                        : ""}
                                </StyledStationListItem>
                            ))}
                    </StyledStationList>
                </StyledListContainer>
            </StationSelectorContainer>
            <ButtonContainer>
                <StyledButton variant="contained" color="primary" onClick={onExit}>{t("home.stationEditor.exit")}</StyledButton>
                <StyledButton
                    bgcolor={ORANGE_500}
                    hovercolor={ORANGE_600}
                    variant="contained"
                    color="primary"
                    onClick={handleUpdate}
                >
                    {t(proceedText)}
                </StyledButton>
            </ButtonContainer>
        </StationEditCreateForm>
    );
};
